// ticket-creation-trend-chart.tsx — Ticket creation volume over time, line chart with period toggle.
import { useState } from 'react';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton, SegmentedControl } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useTicketCreationTrend, type StatDateParams, type StatPeriodParams } from '@/api/tickets-statistics';

type Period = 'day' | 'week' | 'month';

const PERIOD_ITEMS: { value: Period; label: string }[] = [
  { value: 'day',   label: 'Day'   },
  { value: 'week',  label: 'Week'  },
  { value: 'month', label: 'Month' },
];

const SERIES = [{ key: 'total_created', label: 'Created', color: CHART_COLORS[0] }]; // blue

interface Props {
  dateParams: StatDateParams;
}

export function TicketCreationTrendChart({ dateParams }: Props) {
  const [period, setPeriod] = useState<Period>('month');

  const params: StatPeriodParams = { ...dateParams, period };
  const { data, isLoading, isError } = useTicketCreationTrend(params);

  const rows = (data?.results ?? []).map((d) => ({
    label: d.period,
    total_created: d.total_created,
  }));

  return (
    <Card flat>
      <CardBody>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--vn-space-3)',
          }}
        >
          <CardTitle>Ticket Creation Trend</CardTitle>
          <SegmentedControl
            size="sm"
            aria-label="Period"
            value={period}
            onValueChange={(v) => setPeriod(v as Period)}
            items={PERIOD_ITEMS}
          />
        </div>
        {isError ? (
          <Alert tone="danger" title="Failed to load creation trend data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 260 }} />
        ) : rows.length === 0 ? (
          <EmptyState title="No data" description="No ticket creation data in this date range." />
        ) : (
          <Chart
            type="line"
            data={rows}
            xKey="label"
            series={SERIES}
            height={260}
            showGrid
            showXAxis
            showYAxis
            curve="monotone"
            valueFormatter={(v) => String(v ?? 0)}
          />
        )}
      </CardBody>
    </Card>
  );
}
